import { Box, ClassNameMap, Divider, Typography } from "@mui/material";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { useQuery } from "react-query";

import { getStatusClassName, getTextFieldClassName } from "../utils";
import { RichFormattedMessage } from "./RichFormattedMessage";
import { TextFormDetails } from "../interfaces";
import { getBatchInfos } from "../../Dashboard/DashboardBatch";
import { StatusIcon } from "./StatusMessage";

const useConditionalTextRenderer = (
  conditionalTextRenderer: TextFormDetails["conditionalTextRenderer"],
  row?: GridRowModel
): boolean => {
  const showIfBatchRan = conditionalTextRenderer?.showIfBatchRan ?? false;

  const { data: batchStatus } = useQuery(["batch-status"], getBatchInfos, {
    enabled: showIfBatchRan,
  });

  if (!conditionalTextRenderer) return true;

  const isBatchRanConditionMet = (): boolean => {
    if (!showIfBatchRan) return true;
    const lastBatchDate = batchStatus?.last_batch_date;
    const updatedAt = row?.updated_at;
    if (!lastBatchDate || !updatedAt) return false;
    return new Date(lastBatchDate) > new Date(updatedAt);
  };

  return [isBatchRanConditionMet].every((check) => check());
};

const TextMessage = ({
  field,
  classes,
  row,
}: {
  field: TextFormDetails;
  classes: ClassNameMap;
  row?: GridRowModel;
}) => {
  const shouldRender = useConditionalTextRenderer(
    field.conditionalTextRenderer,
    row
  );

  if (!shouldRender) return null;

  const statusClassName = field.status
    ? getStatusClassName(field.status)
    : undefined;

  const className = getTextFieldClassName(
    classes,
    field.className,
    statusClassName
  );

  return (
    <>
      {field.separator && (
        <Divider
          sx={{
            borderColor: "divider",
            width: "60%",
            mt: 1,
            mx: "auto",
            mb: 2,
          }}
        />
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        {field.status && field.icon && <StatusIcon status={field.status} />}
        <Typography
          component="p"
          className={className}
          sx={{
            textAlign: "center",
            margin: 0,
            fontSize: "0.812rem",
            fontStyle: "italic",
            ...field.sx,
          }}
        >
          <RichFormattedMessage id={field.label} />
        </Typography>
      </Box>
    </>
  );
};

export default TextMessage;
